import { DateTime } from 'luxon';

import PurchaseTypes from '@/common/enums/purchase-types.enum';
import { UnitInfo } from '@/common/models';
import LineItem from '@/common/models/lineitem/LineItem';
import LineItemCreative from '@/common/models/lineitem/LineItemCreative';
import LineItemProperty from '@/common/models/lineitem/LineItemProperty';
import LineItemTargetingCriterion from '@/common/models/lineitem/LineItemTargetingCriterion';
import PackageGroup from '@/common/models/package-groups/PackageGroup';
import LanguageUnitRatioStatuses from '@/common/models/unit/LanguageUnitRatioStatuses';
import ErrorHandler from '@/ErrorHandler';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { toServerDate } from '@/globals';
import API from '@/org-app/api';
import rootStore from '@/store';
import * as modelTypes from '@/store/modules/models/mutation-types';
import { createApiAction, createGetAllAction, createPatchAction, createPutAction } from '@/utils/ActionsUtils';
import i18n from '@/vueI18n';

import * as types from './mutation-types';

const actions = {
  getLineItemDefaultRTBMetrics({ commit }, { lineItemId, start, end, keepSeries = false, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      API.instance.getLineItemDefaultRTBMetrics(
        lineItemId,
        { start, end, keep_series: keepSeries },
        (response) => {
          resolve(response);
          if (showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
        (error) => {
          const msg = 'FAIL';
          ErrorHandler.showError(msg, error);
          reject(error);
          if (showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
      );
    });
  },

  getLineItemRTBMetricsForDay({ commit }, { lineItemId, day, keepSeries = false }) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      API.instance.getLineItemRTBMetricsForDay(
        lineItemId,
        day,
        { keep_series: keepSeries },
        (response) => {
          resolve(response);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = 'FAIL';
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getAllLineItems({ commit }) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      API.instance.getAllLineItems(
        (lineItems) => {
          commit(types.UPDATE_LINEITEMS_LIST, lineItems);
          resolve(lineItems);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItems');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getLineItems({ commit }, { queryParams, showLoader = false }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      API.instance.getLineItems(
        queryParams,
        (result) => {
          commit(types.UPDATE_LINEITEMS_LIST, result.items);
          resolve(result);
          if (showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItems');
          ErrorHandler.showError(msg, error);
          reject(error);
          if (showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
      );
    });
  },

  getDeliveryLineItems({ commit }, { queryParams, date }) {
    return new Promise((resolve, reject) => {
      API.instance.getDeliveryLineItems(
        queryParams,
        date,
        (result) => {
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItems');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getCreativesPerLineItem({ commit }, queryParams) {
    return new Promise((resolve, reject) => {
      if (queryParams.showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loading);
      }
      API.instance.getCreativesPerLineItem(
        queryParams,
        (result) => {
          resolve(result);
          if (queryParams.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItemCreatives');
          ErrorHandler.showError(msg, error);
          reject(error);
          if (queryParams.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
      );
    });
  },

  getLineItemsPerCreative({ commit }, { queryParams, showLoader = false }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      API.instance.getLineItemsPerCreative(
        queryParams,
        (result) => {
          commit(types.UPDATE_LINEITEMS_LIST, result.items);
          resolve(result);
          if (queryParams.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetCreativeLineItems');
          ErrorHandler.showError(msg, error);
          reject(error);
          if (queryParams.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
      );
    });
  },

  getLineItem({ commit }, id) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      API.instance.getLineItem(
        id,
        (lineItem) => {
          commit(types.UPDATE_LINEITEM, lineItem);
          resolve(lineItem);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItem');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getLineItemAudit({ commit }, id) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      API.instance.getLineItemAudit(
        id,
        (lineItem) => {
          resolve(lineItem);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItemAudit');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  createNewLineItem({ commit }, { campaign, isSupplyOrganization, organizationSettings, organization }) {
    let purchaseType = isSupplyOrganization ? PurchaseTypes.GUARANTEED : PurchaseTypes.NON_GUARANTEED;
    if (
      organizationSettings != null &&
      organizationSettings.uiDSPAvailablePurchaseTypes.find((x) => x === purchaseType) === -1
    ) {
      [purchaseType] = organizationSettings.uiDSPAvailablePurchaseTypes;
    }

    const lineItem = LineItem.fromObject({
      id: -1,
      campaign,
      campaignID: campaign.id,
      name: '',
      startDate: campaign.startDate != null ? DateTime.fromISO(campaign.startDate) : null,
      endDate: campaign.endDate != null ? DateTime.fromISO(campaign.endDate) : null,
      minSecondsBetweenPlays: 0,
      budgetMode: 'B',
      budget: 0,
      organizationID: organization.id,
      purchaseType,
      hasInfiniteBudget: false, // BE only accept Boolean
    });
    commit(types.CREATE_LINEITEM, lineItem);
    return lineItem;
  },

  async createNewLineItemFromPackage({ commit }, { pkg, campaign }) {
    const obj = {};
    let cpm = null;
    if (!pkg.isCPMFromRateCard) {
      if (pkg.cpmCalculator.computedCPM != null && pkg.cpmCalculator.computedCPM.length > 0) {
        [cpm] = pkg.cpmCalculator.computedCPM;
      } else {
        cpm = pkg.cpmCalculator.cpmFixed;
      }
    }

    obj.campaignID = pkg.campaignID;
    obj.mediaOwnerIDs = pkg.mediaOwnerIDs;
    obj.averageCPM = cpm;
    obj.CPM = cpm;
    obj.cpmNet = pkg.isCPMFromRateCard ? null : pkg.cpmNet;
    obj.startDate = pkg.startDateWhenBuyingFromDeal;
    if (!pkg.numberOfDaysOverridable || pkg.numberOfDaysMin !== pkg.numberOfDaysMax) {
      obj.endDate = DateTime.fromISO(
        DateTime.fromISO(pkg.startDate).plus({
          days: pkg.numberOfDaysMin !== null ? pkg.numberOfDaysMin : pkg.numberOfDaysMax,
        }),
      );
    } else {
      obj.endDate = DateTime.fromISO(pkg.endDate);
    }

    // Make sure the default endDate doesn't exceed the maximum allowed duration for line items
    const maxPossibleEndDate = obj.startDate.plus({ days: LineItem.MaxLineItemDurationInDays });
    obj.endDate = DateTime.min(obj.endDate, maxPossibleEndDate);

    if (!pkg.adDurationOverridable) {
      obj.adDuration = pkg.adDurationMin !== null ? pkg.adDurationMin : pkg.adDurationMax;
    }
    if (!pkg.minSecondsBetweenPlaysOverridable) {
      obj.minSecondsBetweenPlays =
        pkg.minSecondsBetweenPlaysMin !== null ? pkg.minSecondsBetweenPlaysMin : pkg.minSecondsBetweenPlaysMax;
    }
    if (!pkg.budgetOverridable) {
      if (pkg.goal === 'impressions' && !pkg.isCPMFromRateCard) {
        obj.budget = (pkg.marketTargetedImpressionsMin / 1000) * pkg.cpmCalculator.computedCPM[0];
      } else {
        obj.budget = pkg.budgetMin !== null ? pkg.budgetMin : pkg.budgetMax;
      }
    } else if (pkg.goal === 'budget') {
      obj.budget = pkg.budgetMin !== null ? pkg.budgetMin : pkg.budgetMax;
    }
    if (!pkg.marketTargetedImpressionsOverridable) {
      if (pkg.goal === 'budget' && !pkg.isCPMFromRateCard) {
        obj.targetImpressions = pkg.cpmFixed > 0 ? Math.round((pkg.budgetMin / pkg.cpmFixed) * 1000) : 0;
      } else {
        obj.targetImpressions =
          pkg.marketTargetedImpressionsMin !== null
            ? pkg.marketTargetedImpressionsMin
            : pkg.marketTargetedImpressionsMax;
      }
    } else if (pkg.goal === 'impressions') {
      obj.targetImpressions =
        pkg.marketTargetedImpressionsMin !== null ? pkg.marketTargetedImpressionsMin : pkg.marketTargetedImpressionsMax;
    }
    if (!pkg.networksOverridable || pkg.isPreferredOrNGInternalDeal) {
      obj.networkIDs = pkg.networkIDs;
    }
    if (!pkg.locationTargetsOverridable || pkg.isPreferredOrNGInternalDeal) {
      obj.locationIDs = pkg.locationTargetIDs;
      obj.locationExclusionIDs = pkg.locationExclusionIDs;
      obj.siteIDs = pkg.siteIDs;
      obj.siteExclusionIDs = pkg.siteExclusionIDs;
      obj.unitIDs = pkg.unitIDs;
      obj.unitExclusionIDs = pkg.unitExclusionIDs;
      obj.marketIDs = pkg.marketIDs;
      obj.geofenceGroupIDs = pkg.geofenceGroupIDs;
      obj.unitTags = pkg.unitTags;
      obj.unitTagExclusions = pkg.unitTagExclusions;
      obj.creativeFormats = pkg.creativeFormats;
      obj.creativeFormatExclusions = pkg.creativeFormatExclusions;
    }
    if (!pkg.unitAssignmentsOverridable || pkg.isPreferredOrNGInternalDeal) {
      obj.unitIDs = pkg.unitIDs;
      obj.unitExclusionIDs = pkg.unitExclusionIDs;
    }
    if (!pkg.mediaTypesOverridable || pkg.isPreferredOrNGInternalDeal) {
      obj.mediaTypeIDs = pkg.mediaTypeIDs;
    }
    if (!pkg.demographicTargetsOverridable) {
      obj.demographicIDs = pkg.demographicTargetIDs;
    }
    if (!pkg.daypartOverridable) {
      obj.daypart = pkg.daypart;
    }
    obj.unitPackExclusionIDs = pkg.unitPackExclusionIDs;
    obj.unitPackIDs = pkg.unitPackIDs;
    obj.lineItemProperties = pkg.packageProperties?.map((x) => new LineItemProperty(null, null, x.key, x.value));
    obj.isCPMFromRateCard = pkg.isCPMFromRateCard;
    obj.name = pkg.name;
    obj.packageID = pkg.id;
    obj.package = pkg;
    obj.purchaseType = pkg.purchaseType;

    if (pkg.packageTargetingCriteria != null && pkg.packageTargetingCriteria.length > 0) {
      obj.lineItemTargetingCriteria = pkg.packageTargetingCriteria.map((x) => {
        return new LineItemTargetingCriterion(-1, -1, x.targetingCriterionID, x.values);
      });
    }
    obj.campaign = campaign;

    const lineItem = LineItem.fromObject(obj);
    commit(types.CREATE_LINEITEM, lineItem);
    return lineItem;
  },

  convertPlanEntityToLineItemEntity(_, { plan, entity, isUsingBudgetBreakdown, lineItem, campaign }) {
    return {
      // Line item info
      name: isUsingBudgetBreakdown ? lineItem.name : `${plan.name} - LI #1`,
      campaignID: campaign?.id,
      startDate: toServerDate(plan.startDate),
      endDate: toServerDate(plan.endDate),

      // Delivery
      purchaseType: entity.purchaseType,
      budgetMode: entity.budgetMode,
      budget: entity.budgetMode === 'B' && entity.budget == null ? 0 : entity.budget,
      targetImpressions: entity.budgetMode === 'I' && entity.targetImpressions == null ? 0 : entity.targetImpressions,

      // CPM
      cpmGross: entity.cpmGross,
      CPM: entity.CPM,
      cpmNet: entity.cpmNet,
      isCPMFromRateCard: entity.isCPMFromRateCard,

      // Inventory Targeting
      networkIDs: entity.networkIDs,
      networkExclusionIDs: entity.networkExclusionIDs,
      locationIDs: entity.locationIDs,
      locationExclusionIDs: entity.locationExclusionIDs,
      siteIDs: plan.siteIDs,
      siteExclusionIDs: plan.siteExclusionIDs,
      unitIDs: plan.unitIDs,
      unitExclusionIDs: plan.unitExclusionIDs,
      unitPackIDs: plan.unitPackIDs,
      unitPackExclusionIDs: plan.unitPackExclusionIDs,
      geofenceGroupIDs: plan.geofenceGroupIDs,
      geofenceGroupExclusionIDs: plan.geofenceGroupExclusionIDs,
      marketIDs: entity.marketIDs,
      marketExclusionIDs: entity.marketExclusionIDs,
      mediaTypeIDs: entity.mediaTypeIDs,
      mediaTypeExclusionIDs: entity.mediaTypeExclusionIDs,
      unitTags: plan.unitTags,
      unitTagExclusions: plan.unitTagExclusions,
      creativeFormats: plan.creativeFormats,
      creativeFormatExclusions: plan.creativeFormatExclusions,
      languageIDs: plan.languageIDs,
      languageExclusionIDs: plan.languageExclusionIDs,
      creativeOrientations: plan.creativeOrientations,
      creativeOrientationExclusions: plan.creativeOrientationExclusions,
      iabCategories: plan.iabCategories,
      iabCategoryExclusions: plan.iabCategoryExclusions,

      // Deals
      packageID: entity.packageID,
      packageGroupID: entity.packageGroupID,
      packageIDs: entity.packageIDs,
      buyFromDealsEnabled: entity.buyFromDealsEnabled,

      // Other targeting
      daypart: plan.daypart,
      demographicIDs: plan.demographicIDs,
      mediaOwnerIDs: entity.mediaOwnerIDs,
      adDuration: plan.adDuration,
      minSecondsBetweenPlays: parseInt(plan.minSecondsBetweenPlays, 10),
      lineItemProperties: plan.planProperties?.map((x) => new LineItemProperty(null, null, x.key, x.value)),
      networkFrequencyCaps: plan.networkFrequencyCaps,
      minConcentrationFactor: plan.minConcentrationFactor,
      minConcentrationValue: plan.minConcentrationValue,
      lineItemTargetingCriteria:
        plan.planTargetingCriteria?.map(
          (x) => new LineItemTargetingCriterion(-1, -1, x.targetingCriterionID, x.values),
        ) || undefined,
      maxBidCPM: plan.maxBidCPM,
      isMeasurementRequested: plan.isMeasurementRequested,
    };
  },

  async createNewLineItemFromPlan({ commit }, { plan, campaign }) {
    const obj = actions.convertPlanEntityToLineItemEntity(null, {
      plan,
      campaign,
      entity: plan,
    });

    // CPM
    let cpm = null;
    if (!plan.isCPMFromRateCard) {
      if (plan.cpmCalculator.computedCPM != null && plan.cpmCalculator.computedCPM.length > 0) {
        [cpm] = plan.cpmCalculator.computedCPM;
      } else {
        cpm = plan.cpmCalculator.cpmFixed;
      }
    }
    obj.averageCPM = cpm;
    obj.CPM = cpm;
    obj.cpmNet = plan.isCPMFromRateCard ? null : plan.cpmNet;

    // Date restriction
    obj.startDate = plan.startDate < DateTime.local() ? DateTime.local() : plan.startDate;
    const planEndDate = plan.endDate < DateTime.local() ? DateTime.local().plus({ days: 7 }) : plan.endDate;
    // Make sure the default endDate doesn't exceed the maximum allowed duration for line items
    const maxPossibleEndDate = obj.startDate.plus({ days: LineItem.MaxLineItemDurationInDays });
    obj.endDate = DateTime.min(DateTime.fromISO(planEndDate), maxPossibleEndDate);

    const lineItem = LineItem.fromObject(obj);
    commit(types.CREATE_LINEITEM, lineItem);
    return lineItem;
  },

  deleteLineItems({ commit }, lineItems) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      let successfullApiCalls = 0;
      let failedApiCalls = 0;

      const evalCompletion = () => {
        if (successfullApiCalls + failedApiCalls === lineItems.length) {
          if (successfullApiCalls === lineItems.length) {
            resolve(true);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          } else {
            reject();
            const msg = i18n.global.t('messages.failedToDeleteLineItems');
            ErrorHandler.showError(msg);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        }
      };

      lineItems.forEach((lineItem) => {
        API.instance.deleteLineItem(
          lineItem.id,
          () => {
            commit(types.DELETE_LINEITEM, lineItem);
            successfullApiCalls += 1;
            evalCompletion();
          },
          () => {
            failedApiCalls += 1;
            evalCompletion();
          },
        );
      });
    });
  },

  deleteLineItem({ commit }, lineItem) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.deleteLineItem(
        lineItem.id,
        () => {
          commit(types.DELETE_LINEITEM, lineItem);
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToDeleteLineItem');
          ErrorHandler.showError(msg, error);
          reject();
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getLineItemSummaryDeliveryStats({ commit }, lineItemAndDateRange) {
    return new Promise((resolve, reject) => {
      API.instance.getLineItemSummaryDeliveryStats(
        lineItemAndDateRange.lineItem.id,
        (stats) => {
          commit(
            modelTypes.UPDATE_LINEITEM_DELIVERY_HOURLY_STATS,
            {
              lineItem: lineItemAndDateRange.lineItem,
              value: stats,
            },
            { root: true },
          );
          resolve(stats);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItemSummaryDeliveryStats');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getLineItemSummaryPanel({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getLineItemSummaryPanel(
        params,
        (stats) => {
          resolve(stats);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItemSummaryPanelStats');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getLineItemDetails({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getLineItemDetails(
        params,
        (stats) => {
          resolve(stats);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItemDetailsStats');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getLineItemSites({ commit }, params) {
    if (params.showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      API.instance.getLineItemSites(
        params,
        (stats) => {
          if (params.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
          resolve(stats);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItemSitesStats');
          ErrorHandler.showError(msg, error);
          if (params.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
          reject(error);
        },
      );
    });
  },

  getLineItemUnits({ commit }, params) {
    if (params.showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      API.instance.getLineItemUnits(
        params,
        (stats) => {
          if (params.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
          resolve(stats);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItemUnitsStats');
          ErrorHandler.showError(msg, error);
          if (params.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
          reject(error);
        },
      );
    });
  },

  getLineItemTimeline({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getLineItemTimeline(
        params,
        (stats) => {
          resolve(stats);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItemTimelineStats');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getLineItemHourlyBySite({ commit }, params) {
    if (params.showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      API.instance.getLineItemHourlyBySite(
        params,
        (stats) => {
          if (params.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
          resolve(stats);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItemHourlyStatsBySite');
          ErrorHandler.showError(msg, error);
          if (params.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
          reject(error);
        },
      );
    });
  },

  getLineItemHourlyByUnit({ commit }, params) {
    if (params.showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      API.instance.getLineItemHourlyByUnit(
        params,
        (stats) => {
          if (params.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
          resolve(stats);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItemHourlyStatsByUnit');
          ErrorHandler.showError(msg, error);
          if (params.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
          reject(error);
        },
      );
    });
  },

  getIsDealBought({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getIsDealBought(
        params,
        (stats) => {
          resolve(stats);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetIsDealBought');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  async getDailyLineItemHourlyDeliveryStats({ commit }, { lineItemID, date, showLoader }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const stats = await API.instance.getDailyLineItemHourlyDeliveryStats(lineItemID, date).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetUnitRatioStatuses');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return stats;
  },

  saveLineItem({ commit }, { lineItem, showLoader }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      const successCallback = (data) => {
        commit(types.SAVE_LINEITEM, data);
        resolve(data);
        GlobalEventBus.$emit(GlobalEvents.loaded);
      };

      const errorCallback = (error) => {
        const msg = i18n.global.t('messages.failedToSaveLineItem');
        ErrorHandler.showError(msg, error);
        reject(error);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
      };

      if (lineItem.id === -1) {
        API.instance.postLineItem(lineItem, successCallback, errorCallback);
      }
    });
  },

  saveLineItemState({ commit }, lineItem) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      API.instance.putLineItemState(
        lineItem,
        (data) => {
          commit(types.SAVE_LINEITEM, data);
          resolve(data);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToSaveLineItemState');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  updateLineItem: createPutAction(API.instance.putLineItem, types.SAVE_LINEITEM, 'messages.failedToSaveLineItem'),

  updateLineItemsState({ commit }, { lineItemIDs, newState }) {
    if (lineItemIDs.length <= 0) {
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.updateLineItemsState(
        lineItemIDs,
        newState,
        (result) => {
          commit(types.UPDATE_LINEITEMS_LIST, result);
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToSaveLineItemsState');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  saveLineItemRunState({ commit }, lineItem) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      API.instance.putLineItemRunState(
        lineItem,
        (data) => {
          resolve(data);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToSaveLineItemRunState');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  updateLineItemsRunState({ commit }, { lineItemIDs, newState }) {
    if (lineItemIDs.length <= 0) {
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.updateLineItemsRunState(
        lineItemIDs,
        newState,
        (result) => {
          commit(types.UPDATE_LINEITEMS_LIST, result);
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToSaveLineItemsRunState');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getLineItemCreativeMissingDimensions({ commit }, lineItemIDs) {
    return new Promise((resolve, reject) => {
      API.instance.getLineItemCreativeMissingDimensions(
        lineItemIDs,
        (result) => {
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItemCreatives');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  async getEstimatedDeliveryStatistics({ commit }, { params, cancelTokenSource }) {
    return await API.instance.getEstimatedDeliveryStatistics({ data: params, cancelTokenSource }).catch(({ error }) => {
      const msg = i18n.global.t('messages.failedToGetEstimatedDeliveryStatistics');
      ErrorHandler.showError(msg, error);
      throw error;
    });
  },

  async getEstimatedReachStatistics({ commit }, { params, cancelTokenSource }) {
    return await API.instance.getEstimatedReachStatistics({ data: params, cancelTokenSource });
  },

  async getAudienceConcentration({ commit }, { params, cancelTokenSource }) {
    return await API.instance.getAudienceConcentration({ data: params, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetAudienceConcentration');
      ErrorHandler.showError(msg, err.message, err.status);
      throw err;
    });
  },

  async getEstimatedDeliveryStatisticsScreenAndSiteCount({ commit }, { params }) {
    return await API.instance.getEstimatedDeliveryStatisticsScreenAndSiteCount({ data: params }).catch(({ error }) => {
      const msg = i18n.global.t('messages.failedToGetEstimatedDeliveryStatistics');
      ErrorHandler.showError(msg, error);
      throw error;
    });
  },

  getTargetedUnitAndSiteIDs: createApiAction(
    API.instance.getTargetedUnitAndSiteIDs,
    null,
    'messages.failedToGetEstimatedDeliveryStatistics',
    false,
  ),

  async getUnitsMissingAudienceDataCount({ commit }, { params }) {
    try {
      return (await API.instance.getUnitsMissingAudienceDataCount({ data: params })).unit_count ?? null;
    } catch (err) {
      ErrorHandler.showError(i18n.global.t('messages.failedToGetUnitsMissingAudienceDataCount'), err);
      throw err;
    }
  },

  async getUnitsMissingAudienceDataBrief({ commit }, { params }) {
    try {
      return (
        (await API.instance.getUnitsMissingAudienceDataBrief({ data: params }))?.map((unitInfo) =>
          UnitInfo.fromApi(unitInfo),
        ) ?? []
      );
    } catch (err) {
      ErrorHandler.showError(i18n.global.t('messages.failedToGetUnitsMissingAudienceDataBrief'), err);
      throw err;
    }
  },

  getDailyAndAverageConcentrationPerUnit: createApiAction(
    API.instance.getDailyAndAverageConcentrationPerUnit,
    null,
    'messages.failedToGetDailyAndAverageConcentrationsPerUnit',
  ),

  getDemographicUnitsWithData({ commit }, params) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      API.instance.getDemographicUnitsWithData(
        params,
        (result) => {
          resolve(result);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetDemographicUnitsWithData');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getCPMForTargets({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getCPMForTargets(
        params,
        (result) => {
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItemCPM');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getLineItemCPM({ commit }, lineItem) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      API.instance.getLineItemCPM(
        lineItem,
        (result) => {
          resolve(result);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItemCPM');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getLineItemIsApprovalRequired({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getLineItemIsApprovalRequired(
        params,
        (result) => {
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetLineItemIsApprovalRequired');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  async getUnitsForTargets({ commit }, { params, showLoader = true, cancelTokenSource }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const unitIDs = await API.instance.getUnitsForTargets({ params, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetUnits');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return unitIDs;
  },

  getGeocodedUnitsAndSitesForTargets({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getGeocodedUnitsAndSitesForTargets(
        params,
        (result) => {
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetGeocodedUnitsAndSitesForTargets');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getMissingCreativeDimensionsForTargets({ commit }, params) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      API.instance.getMissingCreativeDimensionsForTargets(
        params,
        (result) => {
          resolve(result);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetMissingCreativeDimensionsForTargets');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getAvails({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getAvails(
        params,
        (result) => {
          resolve(result);
        },
        (error) => {
          // Do not show a notification if we are getting detailed avails because
          // we'll try to get non-detailed avails.
          if (params.get_detailed !== undefined && params.get_detailed === false) {
            const msg = i18n.global.t('messages.failedToGetAvails');
            ErrorHandler.showError(msg, error);
          }
          reject(error);
        },
      );
    });
  },

  getAvailsByUnitPost({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getAvailsByUnitPost(
        params,
        (result) => {
          resolve(result);
        },
        (error) => {
          // Do not show a notification if we are getting detailed avails because
          // we'll try to get non-detailed avails.
          if (params.get_detailed !== undefined && params.get_detailed === false) {
            const msg = i18n.global.t('messages.failedToGetAvails');
            ErrorHandler.showError(msg, error);
          }
          reject(error);
        },
      );
    });
  },

  getAvailsByUnit({ commit }, lineItemId) {
    return new Promise((resolve, reject) => {
      API.instance.getAvailsByUnit(
        lineItemId,
        (result) => {
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetAvails');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getAvailsExecutionById({ commit }, availsExecutionId) {
    return new Promise((resolve, reject) => {
      API.instance.getAvailsExecutionById(
        availsExecutionId,
        (result) => {
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetAvails');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getAvailsDeliveryForecastByPost({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getAvailsDeliveryForecastByPost(
        params,
        (result) => {
          resolve(result);
        },
        (error) => {
          // Do not show a notification if we are getting detailed avails because
          // we'll try to get non-detailed avails.
          if (params.get_detailed !== undefined && params.get_detailed === false) {
            const msg = i18n.global.t('messages.failedToGetAvails');
            ErrorHandler.showError(msg, error);
          }
          reject(error);
        },
      );
    });
  },

  getAvailsDeliveryForecastExecutionById({ commit }, availsExecutionId) {
    return new Promise((resolve, reject) => {
      API.instance.getAvailsDeliveryForecastExecutionById(
        availsExecutionId,
        (result) => {
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetAvails');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  linkCreativesToLineItem({ commit }, change) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);
      const creativeLineItems = change.creatives.map((x) => new LineItemCreative(-1, x.id, change.lineItem.id));
      API.instance.postLineItemCreatives(
        creativeLineItems,
        (result) => {
          commit(modelTypes.UPDATE_LINEITEM_CREATIVES, { lineItem: change.lineItem, value: result }, { root: true });
          GlobalEventBus.$emit(GlobalEvents.loaded);
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToLinkCreativesToLineItem');
          ErrorHandler.showError(msg, error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
          reject(error);
        },
      );
    });
  },

  updateLineItemCreatives({ commit }, lineItemCreatives) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return API.instance
      .updateLineItemCreatives(lineItemCreatives)
      .then((result) => {
        GlobalEventBus.$emit(GlobalEvents.loaded);
        return result;
      })
      .catch((error) => {
        const msg = i18n.global.t('messages.failedToUpdateLineItemCreatives');
        ErrorHandler.showError(msg, error);
        GlobalEventBus.$emit(GlobalEvents.loaded);
        throw error;
      });
  },

  deleteLineItemCreatives({ commit }, lineItemCreatives) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      let successfullApiCalls = 0;
      let failedApiCalls = 0;

      const evalCompletion = (error) => {
        if (successfullApiCalls + failedApiCalls === lineItemCreatives.creatives.length) {
          if (successfullApiCalls === lineItemCreatives.creatives.length) {
            resolve(true);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          } else {
            reject(error);
            const msg = i18n.global.t('messages.failedToDeleteLineItemCreatives');
            ErrorHandler.showError(msg, error);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        }
      };

      lineItemCreatives.creatives.forEach((creative) => {
        API.instance.deleteCreativeLineItems(
          {
            lineItemID: lineItemCreatives.lineItem.id,
            creativeID: creative.id,
          },
          () => {
            commit(types.DELETE_LINEITEM_CREATIVE, {
              lineItemID: lineItemCreatives.lineItem.id,
              creativeID: creative.id,
            });
            successfullApiCalls += 1;
            evalCompletion();
          },
          (error) => {
            failedApiCalls += 1;
            evalCompletion(error);
          },
        );
      });
    });
  },

  deleteLineItemCreative({ commit }, lineItemCreatives) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.deleteCreativeLineItems(
        {
          lineItemID: lineItemCreatives.lineItem.id,
          creativeID: lineItemCreatives.creative.id,
        },
        () => {
          commit(types.DELETE_LINEITEM_CREATIVE, {
            lineItemID: lineItemCreatives.lineItem.id,
            creativeID: lineItemCreatives.creative.id,
          });
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToDeleteCreativeLineItem');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getAllDiscountTypes({ commit }) {
    return new Promise((resolve, reject) => {
      API.instance.getDiscountTypes(
        (discountTypes) => {
          // commit(types.UPDATE_DISCOUNT_TYPES_LIST, discountTypes);
          resolve(discountTypes);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetDiscountTypes');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  approveLineItems({ commit }, lineItems) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      let successfullApiCalls = 0;
      let failedApiCalls = 0;

      const evalCompletion = () => {
        if (successfullApiCalls + failedApiCalls === lineItems.length) {
          if (successfullApiCalls === lineItems.length) {
            resolve(true);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          } else {
            reject();
            const msg = i18n.global.t('messages.failedToApproveLineItems');
            ErrorHandler.showError(msg);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        }
      };

      lineItems.forEach((lineItem) => {
        API.instance.approveLineItem(
          lineItem,
          () => {
            successfullApiCalls += 1;
            evalCompletion();
          },
          () => {
            failedApiCalls += 1;
            evalCompletion();
          },
        );
      });
    });
  },

  approveLineItem({ commit }, lineItem) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.approveLineItem(
        lineItem,
        () => {
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToApproveLineItem');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  rejectLineItems({ commit }, lineItemsAndComments) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      let successfullApiCalls = 0;
      let failedApiCalls = 0;

      const evalCompletion = () => {
        if (successfullApiCalls + failedApiCalls === lineItemsAndComments.length) {
          if (successfullApiCalls === lineItemsAndComments.length) {
            resolve(true);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          } else {
            reject();
            const msg = i18n.global.t('messages.failedToRejectLineItems');
            ErrorHandler.showError(msg);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        }
      };

      lineItemsAndComments.forEach((lineItemAndComment) => {
        API.instance.rejectLineItem(
          lineItemAndComment,
          () => {
            successfullApiCalls += 1;
            evalCompletion();
          },
          () => {
            failedApiCalls += 1;
            evalCompletion();
          },
        );
      });
    });
  },

  rejectLineItem({ commit }, lineItemAndComment) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.rejectLineItem(
        lineItemAndComment,
        () => {
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToRejectLineItem');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getLineItemMediaOwnerApprovals: createGetAllAction(
    API.instance.getLineItemMediaOwnerApprovals,
    null,
    'messages.failedToGetLineItemMediaOwnerApprovals',
    false,
  ),
  getLineItemDemographicTargets: createApiAction(
    API.instance.getLineItemDemographicTargets,
    null,
    'messages.failedToGetLineItemDemographicTargets',
    false,
  ),

  async getUnitRatioStatuses({ commit }, { lineItemIDs, cancelTokenSource, showLoader }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const unitRatioStatuses = await API.instance
      .getUnitRatioStatuses({ lineItemIDs, cancelTokenSource })
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToGetUnitRatioStatuses');
        ErrorHandler.showError(msg, err);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });
    // commit(types.UPDATE_MEDIA_OWNERS_LIST, mediaOwners);

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    const result = Object.keys(unitRatioStatuses)
      .map((lineItemID) => {
        const lurs = unitRatioStatuses[lineItemID].map((x) => LanguageUnitRatioStatuses.fromApi(x));
        lurs.forEach((x) => {
          x.lineItemID = lineItemID;
        });
        return lurs;
      })
      .reduce((mem, x) => [...mem, ...x], []);

    return result;
  },

  async getUnitRatioStatusesForTargets({ commit }, { queryParams, cancelTokenSource, showLoader }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const unitRatioStatuses = await API.instance
      .getUnitRatioStatusesForTargets({ queryParams, cancelTokenSource })
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToGetUnitRatioStatuses');
        ErrorHandler.showError(msg, err);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    if (unitRatioStatuses == null) {
      return null;
    }

    const result = unitRatioStatuses.map((x) => LanguageUnitRatioStatuses.fromApi(x));
    return result;
  },

  patchLineItems: createPatchAction(API.instance.patchLineItems, {
    overall: 'messages.lineItemsBatchOperationFailed',
    adding: 'messages.failedToCreateLineItems',
    updating: 'messages.failedToUpdateLineItems',
    deleting: 'messages.failedToDeleteLineItems',
  }),

  getLineItemCustomFields: createApiAction(API.instance.getLineItemCustomFields),

  async getLineItemCadenceReports({ commit }, { id, period, cancelTokenSource, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }

    const result = await API.instance.getCadenceReports({ id, period, cancelTokenSource }).catch(({ error }) => {
      const msg = i18n.global.t('messages.failedToGetCadenceReport');
      ErrorHandler.showError(msg, error);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw error;
    });
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return result;
  },

  async getDownloadTargetedScreens(_, { type, format, id, cancelTokenSource = null, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const targetedScreen = await API.instance
      .getDownloadTargetedScreens({
        type,
        format,
        id,
        cancelTokenSource,
      })
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToDownloadTargetedScreens');
        ErrorHandler.showError(msg, err.message, err.status);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return targetedScreen;
  },

  async getDownloadTargetedSites(_, { type, format, id, cancelTokenSource = null, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const targetedSites = await API.instance
      .getDownloadTargetedSites({
        type,
        format,
        id,
        cancelTokenSource,
      })
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToDownloadTargetedScreens');
        ErrorHandler.showError(msg, err.message, err.status);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return targetedSites;
  },

  getReachAndFrequencyOverall(obj, { params, cancelTokenSource }) {
    return new Promise((resolve, reject) => {
      API.instance.getReachAndFrequencyOverall(
        params,
        cancelTokenSource,
        (result) => {
          resolve(result);
        },
        (error, status) => {
          const msg = i18n.global.t('messages.failedToGetReachAndFrequencyData');
          ErrorHandler.showError(msg, error, status);
          reject(error);
        },
      );
    });
  },

  async saveLineItemPackageGroup({ commit }, { lineItem }) {
    const { packageGroupDealsIDs } = lineItem;
    if (packageGroupDealsIDs == null || packageGroupDealsIDs.length === 0) {
      return; // No package group to create/update;
    }
    // If LI has no PackageGroup, create one and link it to the LI
    if (lineItem.packageGroupID == null) {
      const packageGroup = new PackageGroup(new Set(packageGroupDealsIDs));
      // 1 - Create the package group
      const result = await rootStore.dispatch('orgApp/createPackageGroup', { instances: packageGroup });
      const packageGroupID = result.id;
      // 2 - Assign the created package group ID to the LineItem
      await rootStore.dispatch('updateLineItemPackageGroupID', {
        lineItem,
        value: packageGroupID,
      });
    } else {
      // Else, update the existing Package Group with the new deals
      const updatedPackageGroup = new PackageGroup(new Set(packageGroupDealsIDs), lineItem.packageGroupID);
      await rootStore.dispatch('orgApp/updatePackageGroup', { instance: updatedPackageGroup });
    }
  },
};

export default actions;
